import { useEffect, useState } from 'react';

import api from '../../api.js';
import { getQuicksightDashboards } from '../../config/server-config.js';

export interface QuicksightDashboardListItem {
  dashboardId: string;
  name: string;
}

let cache: QuicksightDashboardListItem[] | null = null;
let inflight: Promise<QuicksightDashboardListItem[]> | null = null;

async function fetchDashboardsOnce(): Promise<QuicksightDashboardListItem[]> {
  if (cache != null) return cache;
  if (inflight != null) return inflight;
  inflight = (async () => {
    const res = await api.get<{ success?: boolean; dashboards?: QuicksightDashboardListItem[] }>(
      getQuicksightDashboards,
    );
    const list = Array.isArray(res.data?.dashboards) ? res.data.dashboards : [];
    cache = list;
    return list;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/** Cached list from GET /api/v1/quicksight/dashboards (same-origin session). */
export function useQuicksightDashboards(enabled: boolean): {
  dashboards: QuicksightDashboardListItem[];
  loading: boolean;
  error: string | null;
} {
  const [dashboards, setDashboards] = useState<QuicksightDashboardListItem[]>(() =>
    enabled && cache != null ? cache : [],
  );
  const [loading, setLoading] = useState(enabled && cache == null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setDashboards([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (cache != null) {
      setDashboards(cache);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDashboardsOnce()
      .then((list) => {
        if (!cancelled) {
          setDashboards(list);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load dashboards');
          setDashboards([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { dashboards, loading, error };
}

export function invalidateQuicksightDashboardsCache(): void {
  cache = null;
}

/** For redirects; uses same cache as the hook. */
export async function fetchQuicksightDashboardsForRedirect(): Promise<QuicksightDashboardListItem[]> {
  return fetchDashboardsOnce();
}
